import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OrganizationalRole } from '../types';
import {
  useOrganizationalRoles,
  useCreateOrganizationalRole,
  useUpdateOrganizationalRole,
  useDeleteOrganizationalRole,
  useSeedOrganizationalRoles,
  useSyncUserRoles,
  useIsAdmin,
} from '../hooks';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/page-header';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Eye, EyeOff, Database, RefreshCw } from 'lucide-react';
import { RoleFormDialog } from '../components/organizational-roles/RoleFormDialog';
import { RolesTable } from '../components/organizational-roles/RolesTable';

export interface RoleFormData {
  name: string;
  isAdmin: boolean;
  displayOrder: string;
  isActive: boolean;
}

const OrganizationalRoles: React.FC = () => {
  useAuth();
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<OrganizationalRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Hooks
  const { data: roles = [], isLoading, refetch } = useOrganizationalRoles(showInactive);
  const createRole = useCreateOrganizationalRole();
  const updateRole = useUpdateOrganizationalRole();
  const deleteRole = useDeleteOrganizationalRole();
  const seedRoles = useSeedOrganizationalRoles();
  const syncUserRoles = useSyncUserRoles();

  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, adminLoading, navigate]);

  const handleOpenDialog = (role?: OrganizationalRole) => {
    setEditingRole(role || null);
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setError(null);
  };

  const handleSubmit = async (formData: RoleFormData) => {
    try {
      const payload = {
        name: formData.name,
        isAdmin: formData.isAdmin,
        displayOrder: parseInt(formData.displayOrder) || 0,
        isActive: formData.isActive,
      };

      if (editingRole) {
        await updateRole.mutateAsync({ id: editingRole.id, data: payload });
        setSuccess('Role updated successfully');
      } else {
        await createRole.mutateAsync(payload);
        setSuccess('Role created successfully');
      }

      handleCloseDialog();
      refetch();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save role');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this role?')) {
      return;
    }

    try {
      await deleteRole.mutateAsync(id);
      setSuccess('Role deactivated successfully');
      refetch();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate role');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('This will seed the database with default organizational roles. Continue?')) {
      return;
    }

    try {
      await seedRoles.mutateAsync();
      setSuccess('Default roles seeded successfully');
      refetch();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to seed roles. They may already exist.');
    }
  };

  const handleSyncRoles = async () => {
    if (!window.confirm('This will update all users\' admin status based on their organizational role. Continue?')) {
      return;
    }

    try {
      await syncUserRoles.mutateAsync();
      setSuccess('Successfully synced user roles');
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sync user roles');
    }
  };

  if (adminLoading || !isAdmin) {
    return null;
  }

  return (
    <>
      <PageHeader
        title="Organizational Roles"
        description="Manage organizational roles and their admin privileges"
      >
        {roles.length === 0 && !isLoading && (
          <Button
            onClick={handleSeed}
            variant="outline"
            className="gap-2"
          >
            <Database className="h-4 w-4" />
            Seed Default Roles
          </Button>
        )}
        {roles.length > 0 && (
          <Button
            onClick={handleSyncRoles}
            variant="outline"
            className="gap-2"
            disabled={syncUserRoles.isPending}
          >
            <RefreshCw className={`h-4 w-4 ${syncUserRoles.isPending ? 'animate-spin' : ''}`} />
            {syncUserRoles.isPending ? 'Syncing...' : 'Sync User Roles'}
          </Button>
        )}
        <Button
          variant={showInactive ? "default" : "outline"}
          onClick={() => setShowInactive(!showInactive)}
          className="gap-2"
        >
          {showInactive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {showInactive ? 'Hide Inactive' : 'Show Inactive'}
        </Button>
        <Button
          onClick={() => handleOpenDialog()}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Role
        </Button>
      </PageHeader>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 border-green-500 bg-green-50">
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <RolesTable
            roles={roles}
            loading={isLoading}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <RoleFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        editingRole={editingRole}
      />
    </>
  );
};

export default OrganizationalRoles;
